import { Button, Flex, HStack, Select, Spacer, Text } from '@chakra-ui/react';
import React from 'react';
import { FaBackward, FaFastBackward, FaFastForward, FaForward } from 'react-icons/fa';

import UIConfig from '@/config/ui.config';
import { NUMERICAL_ONE, NUMERICAL_ZERO } from '@/constants/defaults';
import { ELEMENT_DATA_TEST_IDS } from '@/tests/testConstants';

interface PaginationControlsProps {
  canNextPage: boolean;
  canPreviousPage: boolean;
  goToPage: (pageIndex: number) => void;
  nextPage: () => void;
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  previousPage: () => void;
  setPageSize: (pageSize: number) => void;
  isZeroBased?: boolean;
  width?: string;
}

const PaginationControls = (props: PaginationControlsProps): JSX.Element => {
  const {
    canNextPage,
    canPreviousPage,
    goToPage,
    isZeroBased = false,
    nextPage,
    pageCount,
    pageIndex,
    pageSize,
    previousPage,
    setPageSize,
    width = '560px',
  } = props;

  const startIndex = isZeroBased ? NUMERICAL_ZERO : NUMERICAL_ONE;
  const endIndex = isZeroBased ? pageCount - NUMERICAL_ONE : pageCount;

  return (
    <Flex alignItems={'center'} className="pagination" justifyContent={'space-between'} marginBottom={'4px'} w={width}>
      <Flex gap={'1'}>
        <Button
          onClick={() => goToPage(startIndex)}
          disabled={!canPreviousPage}
          size={'xs'}
          data-testid={ELEMENT_DATA_TEST_IDS.FIRST_PAGE_BUTTON}
        >
          <FaFastBackward />
        </Button>
        <Spacer />
        <Button
          data-testid={ELEMENT_DATA_TEST_IDS.PREV_PAGE_BUTTON}
          isDisabled={!canPreviousPage}
          onClick={() => canPreviousPage && previousPage()}
          size={'xs'}
        >
          <FaBackward />
        </Button>
        <Spacer />
        <Button
          data-testid={ELEMENT_DATA_TEST_IDS.NEXT_PAGE_BUTTON}
          isDisabled={!canNextPage}
          onClick={() => canNextPage && nextPage()}
          size={'xs'}
        >
          <FaForward />
        </Button>
        <Spacer />
        <Button
          data-testid={ELEMENT_DATA_TEST_IDS.LAST_PAGE_BUTTON}
          disabled={!canNextPage}
          onClick={() => goToPage(endIndex)}
          size={'xs'}
        >
          <FaFastForward />
        </Button>
      </Flex>
      <Spacer />
      <HStack w={'fit-content'}>
        <Text fontSize={'sm'}>Page:</Text>
        <Text data-testid={ELEMENT_DATA_TEST_IDS.PAGINATION_PAGE_COUNT} fontSize={'sm'} fontWeight={'bold'}>
          {pageCount === NUMERICAL_ZERO ? `- of -` : `${pageIndex + NUMERICAL_ONE} of ${pageCount}`}
        </Text>
      </HStack>
      <Spacer />
      <Spacer />
      <Select
        data-testid={ELEMENT_DATA_TEST_IDS.PAGE_SIZE_SELECT}
        h={'24px'}
        onChange={(event) => setPageSize(Number(event.target.value))}
        size={'sm'}
        value={pageSize}
        variant={'outline'}
        w={'fit-content'}
      >
        {UIConfig.PAGINATION_PAGE_SIZE_OPTIONS.map(({ value: pageSize, testingId }) => (
          <option data-testid={testingId} key={pageSize} value={pageSize}>
            Show {pageSize}
          </option>
        ))}
      </Select>
    </Flex>
  );
};

export default PaginationControls;
